// src/lib/riskScore.js
// Client-side risk score calculation + Supabase persistence.
// No serverless function needed — all data is available in the browser.

import { supabase, hasSupabase } from './supabaseClient'

const riskLevelMap    = { low: 10, moderate: 40, elevated: 70, high: 90 }
const assetTypeWeights = { crypto: 90, commodity: 55, stock: 35, etf: 25 }
const riskToleranceMap = { conservative: 20, moderate: 50, aggressive: 80 }
const labelMap = {
  low:      'Balanced · Well diversified',
  moderate: 'Moderate · Some concentration detected',
  elevated: 'Elevated · Review recommended',
  high:     'High · Immediate attention warranted',
}

/**
 * Returns today's cached score, or calculates + saves a new one.
 */
export async function getOrCalculateRiskScore(userId, holdings = [], riskTolerance) {
  if (!hasSupabase || !userId || !holdings.length) return null

  const today = new Date().toISOString().slice(0, 10)

  // Return cached score if already calculated today.
  // .maybeSingle() returns null (not 406) when 0 rows exist.
  const { data: cached } = await supabase
    .from('risk_scores')
    .select('*')
    .eq('user_id', userId)
    .eq('calculated_date', today)
    .maybeSingle()

  if (cached) return cached

  // ── COMPONENT 1: Geo-Political Risk (30%) ──────────────────────────────────
  const countries = [...new Set(holdings.map(h => h.country).filter(Boolean))]
  let geoRisk = 30
  if (countries.length) {
    const { data: geoZones } = await supabase
      .from('country_zone_mappings')
      .select('zone_id, geo_zones(risk_level)')
      .in('country_name', countries)

    const scores = (geoZones ?? []).map(gz => riskLevelMap[gz.geo_zones?.risk_level] ?? 50)
    if (scores.length) geoRisk = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
  }

  // ── COMPONENT 2: Sector Concentration Risk (25%) ───────────────────────────
  const sectorCounts = {}
  for (const h of holdings) {
    if (h.sector) sectorCounts[h.sector] = (sectorCounts[h.sector] ?? 0) + 1
  }
  const maxSectorPct = holdings.length
    ? Math.max(...Object.values(sectorCounts)) / holdings.length
    : 0
  const sectorRisk = Math.min(100, Math.round(maxSectorPct * 130))

  // ── COMPONENT 3: Asset Volatility Risk (20%) ───────────────────────────────
  const volScores = holdings.map(h => assetTypeWeights[h.asset_type] ?? 40)
  const volatilityRisk = Math.round(volScores.reduce((a, b) => a + b, 0) / volScores.length)

  // ── COMPONENT 4: Currency Exposure Risk (15%) ──────────────────────────────
  const currencies = new Set(holdings.map(h => h.currency).filter(Boolean))
  const currencyRisk = currencies.size <= 1 ? 60 : currencies.size >= 4 ? 20 : 35

  // ── COMPONENT 5: Portfolio Balance Risk (10%) ──────────────────────────────
  const targetRisk = riskToleranceMap[riskTolerance] ?? 50
  const balanceRisk = Math.min(100, Math.abs(volatilityRisk - targetRisk))

  // ── FINAL SCORE ────────────────────────────────────────────────────────────
  const score = Math.round(
    geoRisk        * 0.30 +
    sectorRisk     * 0.25 +
    volatilityRisk * 0.20 +
    currencyRisk   * 0.15 +
    balanceRisk    * 0.10
  )

  const band = score <= 30 ? 'low' : score <= 55 ? 'moderate' : score <= 75 ? 'elevated' : 'high'

  const components = {
    'Geo-political exposure': geoRisk,
    'Sector concentration':   sectorRisk,
    'Asset volatility':       volatilityRisk,
    'Currency exposure':      currencyRisk,
    'Portfolio balance':      balanceRisk,
  }
  const primaryDriver = Object.entries(components).sort((a, b) => b[1] - a[1])[0][0]

  // Previous score for delta — .maybeSingle() returns null for new users with no history
  const { data: prev } = await supabase
    .from('risk_scores')
    .select('score')
    .eq('user_id', userId)
    .order('calculated_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  const previousScore = prev?.score ?? null
  const scoreDelta    = previousScore !== null ? score - previousScore : null

  const row = {
    user_id:         userId,
    score,
    band,
    label:           labelMap[band],
    geo_risk:        geoRisk,
    sector_risk:     sectorRisk,
    volatility_risk: volatilityRisk,
    currency_risk:   currencyRisk,
    balance_risk:    balanceRisk,
    primary_driver:  primaryDriver,
    previous_score:  previousScore,
    score_delta:     scoreDelta,
    calculated_date: today,
  }

  // upsert handles the race condition when two components call this simultaneously.
  // ignoreDuplicates: true maps to ON CONFLICT DO NOTHING, which returns 0 rows —
  // so we cannot chain .select() on it (PostgREST 406). Re-fetch after upsert instead.
  await supabase
    .from('risk_scores')
    .upsert(row, { onConflict: 'user_id,calculated_date', ignoreDuplicates: true })

  const { data: saved } = await supabase
    .from('risk_scores')
    .select('*')
    .eq('user_id', userId)
    .eq('calculated_date', today)
    .maybeSingle()

  return saved ?? null
}

/**
 * Returns up to `days` historical scores for sparkline rendering.
 */
export async function getRiskScoreHistory(userId, days = 30) {
  if (!hasSupabase || !userId) return []

  const { data, error } = await supabase
    .from('risk_scores')
    .select('score, band, calculated_at')
    .eq('user_id', userId)
    .order('calculated_at', { ascending: true })
    .limit(days)

  if (error) return []
  return data
}
